import { ContributionMarginYearBreakdown } from "models";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { hasCompletedProjection } from "pages/app/scenario-analysis/utils/keyInsightsHelpers";
import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";
import { scenariosHaveIdenticalCropYears } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { getReferenceScenario } from "pages/app/scenario-analysis/utils/scenarioValidation";

/** Below this a currency baseline is treated as zero, so a percentage against it would be meaningless. */
const MIN_CURRENCY_DENOMINATOR = 1;

/** The same guard for tonnage, where a whole unit would discard real baselines rather than near-zero ones. */
const MIN_YIELD_DENOMINATOR = 0.01;

export interface ScenarioComparison {
  /** The scenario's own cumulative margin over the projection window, in the farm's currency per ha. */
  comparisonCumulativeMargin: number;
  /** The baseline's cumulative margin over the same window, so the card can quote what it is measured against. */
  baselineCumulativeMargin: number;
  /** Scenario minus baseline cumulative margin over the projection window, in the farm's currency per ha. */
  cumulativeMarginDelta: number;
  /** Same delta as a share of the baseline; null when the baseline is effectively zero. */
  cumulativeMarginDeltaPct: number | null;
  /** Average annual yield delta as a share of the baseline; null when the baseline is effectively zero. */
  averageYieldDeltaPct: number | null;
}

const yearsInWindow = (rows: ContributionMarginYearBreakdown[], projectionYears: number): ContributionMarginYearBreakdown[] =>
  rows.filter((row) => row.year >= 1 && row.year <= projectionYears);

/** Sum of annual contribution margin across the projection window. */
export const cumulativeMargin = (rows: ContributionMarginYearBreakdown[], projectionYears: number): number =>
  yearsInWindow(rows, projectionYears).reduce((total, row) => total + bandAvg(row.contributionMargin?.total), 0);

/** Mean annual yield (t DM/ha) over harvested years only: sowing years are zero-filled and would fake a drop. */
export const averageYield = (rows: ContributionMarginYearBreakdown[], projectionYears: number): number => {
  const harvestedRows = yearsInWindow(rows, projectionYears).filter((row) => row.cropId !== null);
  if (harvestedRows.length === 0) return 0;
  const total = harvestedRows.reduce((sum, row) => sum + bandAvg(row.adjustedCropMetrics?.cropYield), 0);
  return total / harvestedRows.length;
};

/** Percentage change against the baseline; null when the baseline is too close to zero to divide by. */
export const percentChange = (scenarioValue: number, baselineValue: number, minDenominator: number = MIN_CURRENCY_DENOMINATOR): number | null => {
  if (Math.abs(baselineValue) < minDenominator) return null;
  return ((scenarioValue - baselineValue) / Math.abs(baselineValue)) * 100;
};

export interface BuildScenarioComparisonInput {
  outcomes: SoilSightOutcomesByScenario | null;
  baselineScenarioId: string;
  comparisonScenarioId: string;
  projectionYears: number;
}

export const buildScenarioComparison = ({
  outcomes,
  baselineScenarioId,
  comparisonScenarioId,
  projectionYears,
}: BuildScenarioComparisonInput): ScenarioComparison => {
  const baselineRows = outcomes?.[baselineScenarioId] ?? [];
  const comparisonRows = outcomes?.[comparisonScenarioId] ?? [];

  const baselineCumulative = cumulativeMargin(baselineRows, projectionYears);
  const comparisonCumulative = cumulativeMargin(comparisonRows, projectionYears);
  const cumulativeMarginDelta = comparisonCumulative - baselineCumulative;

  return {
    comparisonCumulativeMargin: comparisonCumulative,
    baselineCumulativeMargin: baselineCumulative,
    cumulativeMarginDelta,
    cumulativeMarginDeltaPct: percentChange(comparisonCumulative, baselineCumulative),
    averageYieldDeltaPct: percentChange(averageYield(comparisonRows, projectionYears), averageYield(baselineRows, projectionYears), MIN_YIELD_DENOMINATOR),
  };
};

export interface ComparisonSelection {
  baseline: ScenarioData | null;
  /** Every scenario the picker can offer, i.e. all but the baseline. */
  comparisonScenarios: ScenarioData[];
  /** The requested scenario, falling back to the first available one. */
  selected: ScenarioData | null;
}

/** Resolved in one place, so the picker and the figures beside it always describe the same scenario. */
export const resolveComparisonSelection = (scenarios: ScenarioData[], selectedScenarioId: string | null): ComparisonSelection => {
  const baseline = getReferenceScenario(scenarios) ?? null;
  const comparisonScenarios = scenarios.filter((scenario) => scenario.id !== baseline?.id);
  const selected = comparisonScenarios.find((scenario) => scenario.id === selectedScenarioId) ?? comparisonScenarios[0] ?? null;

  return { baseline, comparisonScenarios, selected };
};

/** Both sides have results and the rotations differ — otherwise every figure would read as a real result. */
export const canCompareWithBaseline = (baseline: ScenarioData | null, scenario: ScenarioData | null, outcomes: SoilSightOutcomesByScenario | null): boolean => {
  if (!baseline || !scenario || baseline.id === scenario.id) return false;
  return hasCompletedProjection(baseline.id, outcomes) && hasCompletedProjection(scenario.id, outcomes) && !scenariosHaveIdenticalCropYears(baseline, scenario);
};
